import { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { RotateCcw, Plus, CheckCircle2, Clock, AlertTriangle, Trash2, Pencil } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const empty = () => ({
    merchant: "", reason: "", original_amount: 0, refund_amount: 0,
    original_date: "", expected_refund_date: "", actual_refund_date: "",
    status: "Pending", reference_number: "", remarks: "",
});

const STATUS_STYLE = {
    Pending:      "text-amber-700 bg-amber-50 border-amber-200",
    "In Process": "text-blue-700 bg-blue-50 border-blue-200",
    Received:     "text-emerald-700 bg-emerald-50 border-emerald-200",
    Partial:      "text-blue-700 bg-blue-50 border-blue-200",
    Rejected:     "text-red-700 bg-red-50 border-red-200",
    "Written Off":"text-stone-600 bg-slate-100 border-slate-200",
};

export default function Refunds() {
    const { hasRole } = useAuth();
    const [data, setData] = useState(null);
    const [statuses, setStatuses] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty());
    const [editingId, setEditingId] = useState(null);
    const [busy, setBusy] = useState(false);
    const [filter, setFilter] = useState("all");

    const load = () => {
        api.get("/refunds", filter !== "all" ? { params: { status: filter } } : {}).then((r) => setData(r.data));
        api.get("/refunds/meta").then((r) => setStatuses(r.data?.statuses || []));
    };
    useEffect(() => { load(); }, [filter]);

    const submit = async (e) => {
        e?.preventDefault();
        setBusy(true);
        try {
            const p = { ...form };
            ["original_amount", "refund_amount"].forEach((k) => (p[k] = Number(p[k] || 0)));
            if (editingId) {
                await api.patch(`/refunds/${editingId}`, p);
                toast.success("Refund updated");
            } else {
                await api.post("/refunds", p);
                toast.success("Refund logged");
            }
            setOpen(false); setEditingId(null); setForm(empty()); load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally { setBusy(false); }
    };

    const openAdd = () => { setEditingId(null); setForm(empty()); setOpen(true); };
    const openEdit = (row) => {
        setEditingId(row.id);
        setForm({
            merchant: row.merchant || "",
            reason: row.reason || "",
            original_amount: row.original_amount || 0,
            refund_amount: row.refund_amount || 0,
            original_date: (row.original_date || "").slice(0, 10),
            expected_refund_date: (row.expected_refund_date || "").slice(0, 10),
            actual_refund_date: (row.actual_refund_date || "").slice(0, 10),
            status: row.status || "Pending",
            reference_number: row.reference_number || "",
            remarks: row.remarks || "",
        });
        setOpen(true);
    };

    const markReceived = async (id) => {
        try {
            await api.patch(`/refunds/${id}`, { status: "Received" });
            toast.success("Marked received");
            load();
        } catch (e) { toast.error("Failed"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete refund entry?")) return;
        try { await api.delete(`/refunds/${id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error("Failed"); }
    };

    const today = new Date().toISOString().slice(0, 10);

    return (
        <div data-testid="refunds-page">
            <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="eyebrow flex items-center gap-1.5"><RotateCcw size={11} /> Refund & Reversal Tracker</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Every rupee, back in.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                        Track refunds and reversals with expected vs actual dates, so nothing slips past 30 days unnoticed.
                    </p>
                </div>
                {hasRole("admin", "finance") && (
                    <Button onClick={openAdd} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="refund-add-btn">
                        <Plus size={16} className="mr-1.5" /> Log refund
                    </Button>
                )}
            </div>

            {data && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="kpi-card"><div className="eyebrow flex items-center gap-1.5"><Clock size={11} /> Pending amount</div><div className="font-display text-2xl mt-2 tabular-nums text-amber-700">{fmtMoney(data.total_pending_amount)}</div></div>
                    <div className="kpi-card"><div className="eyebrow flex items-center gap-1.5"><CheckCircle2 size={11} /> Received amount</div><div className="font-display text-2xl mt-2 tabular-nums text-emerald-700">{fmtMoney(data.total_received_amount)}</div></div>
                    <div className="kpi-card"><div className="eyebrow flex items-center gap-1.5"><AlertTriangle size={11} /> Overdue</div><div className={`font-display text-2xl mt-2 tabular-nums ${data.overdue_count > 0 ? "text-red-700" : "text-emerald-700"}`}>{data.overdue_count}</div></div>
                    <div className="kpi-card"><div className="eyebrow">Total tracked</div><div className="font-display text-2xl mt-2 tabular-nums">{data.count}</div></div>
                </div>
            )}

            <div className="flex flex-wrap items-center gap-2 mb-4">
                <button onClick={() => setFilter("all")} className={`px-3 py-1.5 text-xs rounded-full border ${filter === "all" ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 hover:border-slate-400 text-stone-700"}`}>All</button>
                {statuses.map((s) => (
                    <button key={s} onClick={() => setFilter(s)} data-testid={`refund-filter-${s}`} className={`px-3 py-1.5 text-xs rounded-full border ${filter === s ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 hover:border-slate-400 text-stone-700"}`}>{s}</button>
                ))}
            </div>

            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="dense-table w-full">
                    <thead><tr>
                        <th>Merchant</th><th>Reason</th>
                        <th className="text-right">Original ₹</th><th className="text-right">Refund ₹</th>
                        <th>Original date</th><th>Expected</th><th>Actual</th>
                        <th>Ref#</th><th>Status</th><th></th>
                    </tr></thead>
                    <tbody>
                        {(data?.items || []).length === 0 && (
                            <tr><td colSpan={10} className="text-center py-14 text-stone-500">No refunds tracked yet.</td></tr>
                        )}
                        {(data?.items || []).map((r) => {
                            const overdue = r.expected_refund_date && r.expected_refund_date < today && ["Pending", "In Process"].includes(r.status);
                            return (
                                <tr key={r.id} data-testid={`refund-row-${r.id}`}>
                                    <td className="text-stone-900 font-medium">{r.merchant}</td>
                                    <td className="text-xs text-stone-600">{r.reason || "—"}</td>
                                    <td className="text-right tabular-nums text-stone-500">{fmtMoney(r.original_amount)}</td>
                                    <td className="text-right tabular-nums font-medium">{fmtMoney(r.refund_amount)}</td>
                                    <td className="text-xs tabular-nums">{fmtDate(r.original_date)}</td>
                                    <td className={`text-xs tabular-nums ${overdue ? "text-red-700 font-medium" : ""}`}>{fmtDate(r.expected_refund_date)}</td>
                                    <td className="text-xs tabular-nums text-emerald-700">{fmtDate(r.actual_refund_date)}</td>
                                    <td className="text-xs font-mono-figure tabular-nums">{r.reference_number || "—"}</td>
                                    <td><span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${STATUS_STYLE[r.status] || ""}`}>{r.status}</span></td>
                                    <td>
                                        {hasRole("admin", "finance") && (
                                            <div className="flex items-center justify-end gap-1">
                                                <button onClick={() => openEdit(r)} className="p-1 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-900" title="Edit" data-testid={`refund-edit-${r.id}`}>
                                                    <Pencil size={12} />
                                                </button>
                                                {!["Received", "Rejected", "Written Off"].includes(r.status) && (
                                                    <button onClick={() => markReceived(r.id)} className="p-1 rounded hover:bg-emerald-50 text-stone-500 hover:text-emerald-700" title="Mark received" data-testid={`refund-mark-${r.id}`}>
                                                        <CheckCircle2 size={13} />
                                                    </button>
                                                )}
                                                {hasRole("admin") && (
                                                    <button onClick={() => remove(r.id)} className="p-1 rounded hover:bg-red-50 text-stone-500 hover:text-red-600" title="Delete" data-testid={`refund-delete-${r.id}`}><Trash2 size={12} /></button>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(empty()); } }}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>{editingId ? "Edit refund / reversal" : "Log refund / reversal"}</DialogTitle></DialogHeader>
                    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div><Label className="eyebrow">Merchant</Label><Input value={form.merchant} onChange={(e) => setForm((f) => ({ ...f, merchant: e.target.value }))} required data-testid="refund-form-merchant" /></div>
                        <div><Label className="eyebrow">Reason</Label><Input value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Cancelled booking / returned item…" /></div>
                        <div><Label className="eyebrow">Original amount (₹)</Label><Input type="number" value={form.original_amount} onChange={(e) => setForm((f) => ({ ...f, original_amount: e.target.value }))} /></div>
                        <div><Label className="eyebrow">Refund amount (₹)</Label><Input type="number" value={form.refund_amount} onChange={(e) => setForm((f) => ({ ...f, refund_amount: e.target.value }))} required data-testid="refund-form-amount" /></div>
                        <div><Label className="eyebrow">Original transaction date</Label><Input type="date" value={form.original_date} onChange={(e) => setForm((f) => ({ ...f, original_date: e.target.value }))} /></div>
                        <div><Label className="eyebrow">Expected refund date</Label><Input type="date" value={form.expected_refund_date} onChange={(e) => setForm((f) => ({ ...f, expected_refund_date: e.target.value }))} /></div>
                        <div><Label className="eyebrow">Reference #</Label><Input value={form.reference_number} onChange={(e) => setForm((f) => ({ ...f, reference_number: e.target.value }))} /></div>
                        <div>
                            <Label className="eyebrow">Status</Label>
                            <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div><Label className="eyebrow">Actual refund date</Label><Input type="date" value={form.actual_refund_date} onChange={(e) => setForm((f) => ({ ...f, actual_refund_date: e.target.value }))} /></div>
                        <div className="md:col-span-2"><Label className="eyebrow">Remarks</Label><Textarea rows={2} value={form.remarks} onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))} /></div>
                        <DialogFooter className="md:col-span-2">
                            <Button type="button" variant="outline" onClick={() => { setOpen(false); setEditingId(null); }}>Cancel</Button>
                            <Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="refund-form-save">
                                {busy ? "Saving…" : editingId ? "Save changes" : "Log refund"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
